import classnames from 'classnames';
import omit from 'omit.js';
// import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {WebUI, getNid} from "../../wui-core/src/index"
import Icon from '../../wui-icon/src/index';
import {setComponentSize} from "../../wui-core/src/componentStyle"
import { TagProps } from './iTag';
import { useConfigContext } from "../../wui-provider/src/context";

// 提取常量到组件外部避免重复创建
const DEFAULT_CLOSE_ICON = <Icon type='uf-close'/>;

const defaultProps = {
    disabled: false,
    deleted: false,
    colors: 'light',
    bordered: false,
    select: false,
    style: {},
    className: '',
    closeIcon: DEFAULT_CLOSE_ICON,
    icon: null,
    size: 'md',
    activeColor: null,
    // type: 'default',
};

const colorsMap: Record<string, string> = {
    dark: 'dark',
    light: 'light',
    success: 'success',
    warning: 'warning',
    danger: 'danger',
    info: 'info',
    invalid: 'invalid',
    start: 'start',
    'half-blue': 'half-blue',
    'half-green': 'half-green',
    'half-dark': 'half-dark',
    'half-yellow': 'half-yellow',
    'half-red': 'half-red',
    '#6366F1': 'purple',
    '#84CC16': 'green',
    '#F97316': 'orange',
    '#EC4899': 'pink',
};

const TagComponent: React.FC<TagProps> = (props) => {
    // 简化状态初始化，避免不必要的函数创建
    const [selected, setSelected] = useState(props.selected || false);
    const [visible, setVisible] = useState(props.visible ?? true);

    // 替换 getDerivedStateFromProps
    useEffect(() => {
        if ('selected' in props && props.selected !== undefined) {
            setSelected(props.selected);
        }
    }, [props.selected]);

    useEffect(() => {
        if ('visible' in props && props.visible !== undefined) {
            setVisible(props.visible);
        }
    }, [props.visible]);

    // 优化useCallback，使用可选链操作符
    const click = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
        if (!props.disabled) {
            setSelected(prev => !prev);
            props.tagClick?.(e);
        }
    }, [props.disabled, props.tagClick]);

    const onDelete = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
        if (!props.disabled) {
            setVisible(prev => !prev);
            props.onClose?.(e);
        }
    }, [props.disabled, props.onClose]);

    const configContext = useConfigContext({name: 'tag'});

    let mergeProps = {...defaultProps, ...configContext, ...props};

    let {
        colors,
        disabled,
        deleted,
        select,
        className,
        bordered,
        children,
        clsPrefix,
        closeIcon,
        icon,
        size,
        fieldid,
        activeColor,
        type = 'default',
        // visible,
        // onClose,//不需要渲染到dom上
        // tagClick,//不需要渲染到dom上
        ...others
    } = mergeProps;

    const isSelected = select && selected; // 是否选中态

    // 使用useMemo缓存className计算，避免每次重新计算
    const classes = useMemo(() => {
        return classnames(
            clsPrefix,
            {
                [`${clsPrefix}-${colorsMap[colors!]}`]: !!colorsMap[colors!],
                [`${clsPrefix}-filled`]: type === 'filled',
                [`${clsPrefix}-border`]: (typeof bordered === 'boolean' ? bordered : false) || type === 'bordered',
                [`${clsPrefix}-select`]: select,
                [`${clsPrefix}-selected`]: isSelected, // "选择标签"选中时，改变标签的样式，单独写了一个selected类
                [`${clsPrefix}-delete`]: deleted,
                [`${clsPrefix}-icon`]: !!icon,
                [`${clsPrefix}-${setComponentSize(size)}`]: true,
                [`${clsPrefix}-active-${colorsMap[activeColor!]}`]: isSelected && !!colorsMap[activeColor!],
                // [`${clsPrefix}-colors`]: colors && !colorsMap[colors],
            },
            className,
        );
    }, [clsPrefix, colors, type, bordered, select, isSelected, deleted, icon, size, activeColor, className]);

    // 使用useMemo缓存style计算，避免每次创建新对象
    const style = useMemo(() => {
        let baseStyle = others.style || {};

        // 如果colors不在colorsMap中，则作为自定义颜色处理
        if (colors && !colorsMap[colors]) {
            baseStyle = {
                ...baseStyle,
                backgroundColor: colors,
                color: '#fff'
            };
        }

        // 如果选中状态且有自定义activeColor
        if (isSelected && activeColor && !colorsMap[activeColor]) {
            baseStyle = {
                ...baseStyle,
                backgroundColor: activeColor,
            };
        }

        return baseStyle;
    }, [others.style, colors, isSelected, activeColor]);
    let adapterNid = getNid(props);

    return (
        visible ? (
            <span {...omit(others, ['visible', 'onClose', 'tagClick'])} fieldid={fieldid} style={style} className={classes}
                disabled={disabled} onClick={click} {...adapterNid}>
                {icon && <span className="icon">{icon}</span>}
                {children}
                {deleted && <span onClick={onDelete} fieldid={fieldid && `${fieldid}_close`}>{closeIcon}</span>}
            </span>
        ) : null
    );
};

// 使用React.memo优化组件，避免不必要的重渲染
const Tag = React.memo(TagComponent, (prevProps, nextProps) => {
    // 自定义比较逻辑，只比较关键props
    const keysToCompare: (keyof TagProps)[] = [
        'selected', 'visible', 'colors', 'disabled', 'deleted', 'select',
        'bordered', 'children', 'className', 'icon', 'size', 'activeColor',
        'type', 'tagClick', 'onClose', 'style'
    ];

    return keysToCompare.every(key => prevProps[key] === nextProps[key]);
});

(Tag as any).defaultProps = {...defaultProps};

export default WebUI({name: "tag", defaultProps})(TagComponent)
